import type { Loc } from './cards.ts'

export interface SignDef {
  id: string
  glyph: string
  ru: string
  az: string
  en: string
  tr: string
}

export const SIGNS: SignDef[] = [
  { id: 'aries',       glyph: '♈', ru: 'Овен',      az: 'Qoç', en: 'Aries', tr: 'Koç' },
  { id: 'taurus',      glyph: '♉', ru: 'Телец',     az: 'Buğa', en: 'Taurus', tr: 'Boğa' },
  { id: 'gemini',      glyph: '♊', ru: 'Близнецы',  az: 'Əkizlər', en: 'Gemini', tr: 'İkizler' },
  { id: 'cancer',      glyph: '♋', ru: 'Рак',       az: 'Xərçəng', en: 'Cancer', tr: 'Yengeç' },
  { id: 'leo',         glyph: '♌', ru: 'Лев',       az: 'Şir', en: 'Leo', tr: 'Aslan' },
  { id: 'virgo',       glyph: '♍', ru: 'Дева',      az: 'Qız', en: 'Virgo', tr: 'Başak' },
  { id: 'libra',       glyph: '♎', ru: 'Весы',      az: 'Tərəzi', en: 'Libra', tr: 'Terazi' },
  { id: 'scorpio',     glyph: '♏', ru: 'Скорпион',  az: 'Əqrəb', en: 'Scorpio', tr: 'Akrep' },
  { id: 'sagittarius', glyph: '♐', ru: 'Стрелец',   az: 'Oxatan', en: 'Sagittarius', tr: 'Yay' },
  { id: 'capricorn',   glyph: '♑', ru: 'Козерог',   az: 'Oğlaq', en: 'Capricorn', tr: 'Oğlak' },
  { id: 'aquarius',    glyph: '♒', ru: 'Водолей',   az: 'Dolça', en: 'Aquarius', tr: 'Kova' },
  { id: 'pisces',      glyph: '♓', ru: 'Рыбы',      az: 'Balıqlar', en: 'Pisces', tr: 'Balık' },
]

export function signById(id: string): SignDef {
  return SIGNS.find((s) => s.id === id) ?? SIGNS[4]
}

/** Подписи сфер интересов для промпта — в родительном/винительном контексте. */
export const GOAL_PROMPT_LABELS: Record<string, Loc> = {
  love: { ru: 'любовь и отношения', az: 'sevgi və münasibətlər',
          en: 'love and relationships', tr: 'aşk ve ilişkiler' },
  career: { ru: 'карьера и призвание', az: 'karyera və peşə',
            en: 'career and calling', tr: 'kariyer ve meslek' },
  money: { ru: 'деньги и достаток', az: 'pul və bolluq',
           en: 'money and abundance', tr: 'para ve bolluk' },
  growth: { ru: 'саморазвитие', az: 'inkişaf',
            en: 'personal growth', tr: 'kişisel gelişim' },
  health: { ru: 'здоровье и энергия', az: 'sağlamlıq və enerji',
            en: 'health and energy', tr: 'sağlık ve enerji' },
  destiny: { ru: 'путь и предназначение', az: 'yol və məqsəd',
             en: 'path and purpose', tr: 'yol ve amaç' },
}

export const GOALS = [
  { id: 'love',    sym: '♡', ru: 'Любовь',       az: 'Sevgi',      ruD: 'Отношения и чувства',  azD: 'Münasibətlər və hisslər',
    en: 'Love', tr: 'Aşk', enD: 'Relationships and feelings', trD: 'İlişkiler ve duygular' },
  { id: 'career',  sym: '☀', ru: 'Карьера',      az: 'Karyera',    ruD: 'Работа и призвание',   azD: 'İş və peşə',
    en: 'Career', tr: 'Kariyer', enD: 'Work and calling', trD: 'İş ve meslek' },
  { id: 'money',   sym: '✧', ru: 'Деньги',       az: 'Pul',        ruD: 'Достаток и финансы',   azD: 'Bolluq və maliyyə',
    en: 'Money', tr: 'Para', enD: 'Means and finances', trD: 'Bolluk ve finans' },
  { id: 'growth',  sym: '☾', ru: 'Саморазвитие', az: 'İnkişaf',    ruD: 'Дух и внутренний рост', azD: 'Ruh və daxili inkişaf',
    en: 'Growth', tr: 'Gelişim', enD: 'Spirit and inner growth', trD: 'Ruh ve içsel gelişim' },
  { id: 'health',  sym: '✚', ru: 'Здоровье',     az: 'Sağlamlıq',  ruD: 'Тело и энергия',       azD: 'Bədən və enerji',
    en: 'Health', tr: 'Sağlık', enD: 'Body and energy', trD: 'Beden ve enerji' },
  { id: 'destiny', sym: '✦', ru: 'Судьба',       az: 'Tale',       ruD: 'Путь и предназначение', azD: 'Yol və məqsəd',
    en: 'Destiny', tr: 'Kader', enD: 'Path and purpose', trD: 'Yol ve amaç' },
]

/**
 * Пол человека.
 *
 * Спрашиваем не ради статистики, а потому что без ответа модель начинает
 * додумывать его сама — и заодно додумывает пол тех, о ком не знает ничего
 * (у конкурента мужчине в отношениях написали «вы ставили ЕЁ чувства выше
 * своих», см. `docs/competitors/faladdin.md`).
 *
 * Грамматика тут пригождается меньше, чем кажется: в азербайджанском и
 * турецком рода нет вовсе, а в русском мы обращаемся на «вы», и согласование
 * прячется почти везде. Пол нужен для существительных о самом человеке
 * («дорогая», «вы сама») и для содержания — любви, отношений, совместимости.
 *
 * `u` — полноценный ответ, а не отсутствие ответа: на нём действует прежний
 * запрет родовых форм.
 */
export type Gender = 'f' | 'm' | 'u'

export const GENDERS = [
  { id: 'f', sym: '♀', ru: 'Женщина', az: 'Qadın', en: 'Woman', tr: 'Kadın' },
  { id: 'm', sym: '♂', ru: 'Мужчина', az: 'Kişi', en: 'Man', tr: 'Erkek' },
  { id: 'u', sym: '✧', ru: 'Не указывать', az: 'Bildirmək istəmirəm',
    en: 'Prefer not to say', tr: 'Belirtmek istemiyorum' },
]

/** Незнакомое значение — «не указан», а не ошибка: пол приходит и от клиента. */
export function toGender(value: unknown): Gender {
  return value === 'f' || value === 'm' ? value : 'u'
}

export const PATHS = [
  { id: 'novice', sym: '☾', ru: 'Новичок',   az: 'Yeni başlayan', ruD: 'Только начинаю свой путь',   azD: 'Yolun başındayam',
    en: 'Beginner', tr: 'Yeni başlayan', enD: 'I am just starting out', trD: 'Yolun henüz başındayım' },
  { id: 'adept',  sym: '✧', ru: 'Практик',   az: 'Təcrübəli',     ruD: 'Иногда обращаюсь к картам',  azD: 'Bəzən kartlara baxıram',
    en: 'Practitioner', tr: 'Deneyimli', enD: 'I turn to the cards now and then', trD: 'Ara sıra kartlara bakarım' },
  { id: 'seer',   sym: '✦', ru: 'Провидец',  az: 'Kahin',         ruD: 'Карты — часть моей жизни',   azD: 'Kartlar həyatımın bir hissəsidir',
    en: 'Seer', tr: 'Kâhin', enD: 'The cards have long been part of my life', trD: 'Kartlar çoktandır hayatımın parçası' },
]
